/**
 * Reference-template helpers for Employee / Global Employee import column mapping
 * (same pattern as GL Sync upload + RefTemplateMappingsEditorDrawer).
 */

export const EMPLOYEE_IMPORT_DOC_TYPE_NAME = 'Employee Import';

/** Canonical payload keys used by employeeimport / globalEmployeeImport. */
export const EMPLOYEE_IMPORT_FIELD_KEYS = [
  'employeeCode',
  'locationName',
  'firstName',
  'lastName',
  'phone',
  'homePhone',
  'hireDate',
  'employmentType',
  'status',
  'department',
  'hourlyRate',
  'jobTitle',
  'jobcodeLevel1',
  'jobcodeLevel2',
];

export const DEFAULT_EMPLOYEE_IMPORT_MAPPING = {
  employeeCode: { targetColumn: 'Employee Code', dataType: 'TEXT', isActive: true },
  locationName: { targetColumn: 'Location Name', dataType: 'TEXT', isActive: true },
  firstName: { targetColumn: 'First Name', dataType: 'TEXT', isActive: true },
  lastName: { targetColumn: 'Last Name', dataType: 'TEXT', isActive: true },
  phone: { targetColumn: 'Mobile Phone', dataType: 'TEXT', isActive: true },
  homePhone: { targetColumn: 'Home Phone', dataType: 'TEXT', isActive: true },
  hireDate: { targetColumn: 'Hire Date', dataType: 'DATE', isActive: true },
  employmentType: { targetColumn: 'Emp Stat', dataType: 'TEXT', isActive: true },
  status: { targetColumn: 'Status', dataType: 'TEXT', isActive: true },
  department: { targetColumn: 'Dept', dataType: 'TEXT', isActive: true },
  hourlyRate: { targetColumn: 'Pay Rate', dataType: 'CURRENCY', isActive: true },
  jobTitle: { targetColumn: 'Job Title', dataType: 'TEXT', isActive: true },
  jobcodeLevel1: { targetColumn: 'Jobcode Level 1', dataType: 'TEXT', isActive: true },
  jobcodeLevel2: { targetColumn: 'Jobcode Level 2', dataType: 'TEXT', isActive: true },
};

export const normalizeHeader = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const normalizeMappingFromApi = (raw) => {
  if (!raw) return {};
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (!key || !value || typeof value !== 'object') return;
    out[String(key).trim()] = {
      targetColumn: value.targetColumn || value.target_column || key,
      dataType: value.dataType || value.data_type || 'TEXT',
      isActive: value.isActive !== false && value.is_active !== false,
    };
  });
  return out;
};

export const toUiRefTemplate = (apiTemplate) => {
  if (!apiTemplate) return null;
  const id = apiTemplate.templateId || apiTemplate.template_id;
  return {
    id: id ? String(id) : '',
    name: apiTemplate.templateName || apiTemplate.template_name || 'Template',
    mappings: normalizeMappingFromApi(apiTemplate.mapping),
    apiTemplate,
  };
};

export const initializeExcelColumnMap = (template, excelFields) => {
  const next = {};
  const fields = Array.isArray(excelFields) ? excelFields.map((f) => String(f ?? '').trim()).filter(Boolean) : [];
  Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, m]) => {
    const templateKey = String(templateKeyRaw).trim();
    if (!m || m.isActive === false) return;
    const expectedHeader = String(m.targetColumn || templateKey).trim();
    const auto = fields.find((f) => normalizeHeader(f) === normalizeHeader(expectedHeader));
    next[templateKey] = auto || '';
  });
  return next;
};

export const buildTemplateColumnMatchRows = (template, excelColumnByTemplateField) => {
  const rows = [];
  Object.entries(template?.mappings || {}).forEach(([templateKey, m]) => {
    if (!m || m.isActive === false) return;
    const expectedHeader = String(m.targetColumn || templateKey).trim();
    const matchedHeader = excelColumnByTemplateField?.[templateKey] || '';
    rows.push({
      templateKey,
      expectedHeader,
      matchedHeader,
      matched: Boolean(matchedHeader),
    });
  });
  return rows;
};

/**
 * Build columnIndexByKey from header row + template field → file header map.
 */
export const buildColumnIndexFromTemplate = (headerRow, excelColumnByTemplateField) => {
  const headerIndexByNorm = {};
  (headerRow || []).forEach((cell, index) => {
    const norm = normalizeHeader(cell);
    if (norm && headerIndexByNorm[norm] === undefined) {
      headerIndexByNorm[norm] = index;
    }
  });
  const columnIndexByKey = {};
  Object.entries(excelColumnByTemplateField || {}).forEach(([key, fileHeader]) => {
    if (!fileHeader) return;
    const idx = headerIndexByNorm[normalizeHeader(fileHeader)];
    if (idx !== undefined) {
      columnIndexByKey[key] = idx;
    }
  });
  return columnIndexByKey;
};

export const fetchEmployeeImportDocumentTypeId = async (baseUrl, tenantId, headers = {}) => {
  if (!tenantId) return null;
  const response = await fetch(
    `${baseUrl}conductor/document-type-tenant-configs/tenant/${encodeURIComponent(tenantId)}?category=REF`,
    { method: 'GET', headers }
  );
  if (!response.ok) return null;
  const list = await response.json();
  const match = (Array.isArray(list) ? list : []).find((item) => {
    const name = item.documentTypeName || item.document_type_name || item.name || '';
    return String(name).trim().toLowerCase() === EMPLOYEE_IMPORT_DOC_TYPE_NAME.toLowerCase();
  });
  return match?.documentTypeId || match?.document_type_id || null;
};

export const fetchEmployeeImportTemplates = async (baseUrl, tenantId, headers = {}) => {
  const documentTypeId = await fetchEmployeeImportDocumentTypeId(baseUrl, tenantId, headers);
  const byId = new Map();

  if (documentTypeId) {
    const response = await fetch(
      `${baseUrl}conductor/ref-templates/by-document-type/${encodeURIComponent(documentTypeId)}`,
      { method: 'GET', headers }
    );
    if (response.ok) {
      const data = await response.json();
      (Array.isArray(data) ? data : []).forEach((row) => {
        const ui = toUiRefTemplate(row);
        if (ui?.id) byId.set(ui.id, ui);
      });
    }
  }

  // Also include templates with no document type (optional doc type / GL system).
  const allResponse = await fetch(`${baseUrl}conductor/ref-templates`, {
    method: 'GET',
    headers,
  });
  if (allResponse.ok) {
    const all = await allResponse.json();
    (Array.isArray(all) ? all : []).forEach((row) => {
      const docId = row.documentTypeId || row.document_type_id || null;
      if (docId) return;
      const ui = toUiRefTemplate(row);
      if (ui?.id) byId.set(ui.id, ui);
    });
  }

  return {
    documentTypeId,
    templates: Array.from(byId.values()),
  };
};
